import { FirebaseError } from 'firebase/app';
import { GeoPoint, Timestamp, addDoc } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useFirestore, useUser } from 'reactfire';

import { AgencyApplication, AgencySubmissionStatus, agencyAppCollection } from 'common';
import { useUploadStorageFiles } from 'hooks';
import { AgencyAppValues } from 'views/AgencyNew';

export const useCreateAgencySubmission = (
  onSuccess?: (subId: string) => void,
  onError?: (msg: string, err: unknown) => void
) => {
  const { data: user } = useUser();
  const firestore = useFirestore();
  const [error, setError] = useState<string | null>(null);
  const { uploadFiles } = useUploadStorageFiles('newAgencySubmissions');

  const handleSubmission = useCallback(
    async (values: AgencyAppValues, sendNotifications: boolean = true) => {
      setError(null);
      try {
        if (!values.EandO || typeof values.EandO === 'string')
          throw new Error('Error uploading E&O');
        const uploadResult = await uploadFiles(values.EandO, {}, `EandO_${values.orgName}_`);
        console.log('uploadResult: ', uploadResult);

        let agencyAppData: AgencyApplication = {
          orgName: values.orgName,
          address: {
            addressLine1: values.address?.addressLine1.trim(),
            addressLine2: values.address?.addressLine2.trim(),
            city: values.address?.city.trim(),
            state: values.address?.state.trim(),
            postal: values.address?.postal.trim(),
            countyName: values.address?.countyName?.trim(),
          },
          contact: {
            firstName: values.contact?.firstName.trim(),
            lastName: values.contact?.lastName.trim(),
            email: values.contact?.email.toLowerCase().trim(),
            phone: values.contact?.phone.trim(),
          },
          agents: values.agents,
          FEIN: values.FEIN.trim(),
          EandO: uploadResult[0].metadata.fullPath,
          status: AgencySubmissionStatus.enum.submitted,
          coordinates:
            values.coordinates?.latitude && values.coordinates?.longitude
              ? new GeoPoint(values.coordinates.latitude, values.coordinates.longitude)
              : null,
          sendAppReceivedNotification: sendNotifications,
          submittedByUserId: user?.uid || null,
          metadata: {
            created: Timestamp.now(),
            updated: Timestamp.now(),
          },
        };

        const docRef = await addDoc(agencyAppCollection(firestore), agencyAppData);

        if (onSuccess) onSuccess(docRef.id);

        return docRef.id;
      } catch (err) {
        console.log('ERROR: ', err);
        let msg = 'Error submitting agency information.';
        if (err instanceof FirebaseError) {
          msg = err.message;
        }
        setError(msg);
        if (onError) onError(msg, err);
      }
    },
    [uploadFiles, onSuccess, onError, firestore, user]
  );

  return { handleSubmission, error };
};
